import React, { useMemo, useState } from 'react';

/**
 * Reusable previous/next visit UI. The parent supplies an episode-scoped,
 * chronologically sorted visit list from the backend, and an async navigation
 * function that runs the save-before-navigation barrier before routing.
 *
 * Visits: [{ clinicalNoteId, noteDate, noteType, status, label? }]
 * onNavigate(visit): Promise<void>
 *
 * This component never creates, finalizes, or signs a note. The backend must
 * authorize every requested note and verify same-episode membership.
 */
export default function ClinicalNoteVisitNavigator({
  visits = [], clinicalNoteId, onNavigate, disabled = false,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ordered = useMemo(() => [...visits].sort((a, b) =>
    String(a.noteDate || '').localeCompare(String(b.noteDate || '')) ||
    Number(a.clinicalNoteId) - Number(b.clinicalNoteId)), [visits]);
  const index = ordered.findIndex(v => String(v.clinicalNoteId) === String(clinicalNoteId));
  const current = index >= 0 ? ordered[index] : null;
  const go = async (visit) => {
    if (!visit || busy || disabled || !onNavigate ||
        String(visit.clinicalNoteId) === String(clinicalNoteId)) return;
    setBusy(true);
    setError('');
    try {
      await onNavigate(visit);
    } catch (e) {
      setError(e?.message || 'Unable to switch visits. Your current note remains open.');
    } finally {
      setBusy(false);
    }
  };
  if (!current || ordered.length < 2) return null;
  return (
    <nav aria-label="Clinical episode visit navigation" style={{ padding: 12, border: '1px solid #ddd', borderRadius: 9, marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" disabled={busy || disabled || index === 0}
          onClick={() => go(ordered[index - 1])}>← Previous Visit</button>
        <label htmlFor="clinical-episode-visit-select" style={{ fontSize: 13, fontWeight: 600 }}>Visit</label>
        <select id="clinical-episode-visit-select" disabled={busy || disabled}
          value={String(current.clinicalNoteId)}
          onChange={event => go(ordered.find(v => String(v.clinicalNoteId) === event.target.value))}>
          {ordered.map(v => (
            <option key={v.clinicalNoteId} value={String(v.clinicalNoteId)}>
              {v.noteDate || 'Unknown date'} — {v.label || v.noteType || 'Clinical Note'} ({v.status || 'Unknown'})
            </option>
          ))}
        </select>
        <button type="button" disabled={busy || disabled || index === ordered.length - 1}
          onClick={() => go(ordered[index + 1])}>Next Visit →</button>
        {busy && <span role="status">Saving before switching…</span>}
      </div>
      {error && <div role="alert" style={{ marginTop: 8, color: '#b42318' }}>{error}</div>}
    </nav>
  );
}
